import {
  actionButton,
  createElement,
  displayValue,
  labeledField,
  replaceChildren,
  safeImageUrl,
  svgIcon,
  toText
} from './dom.js';

const TYPE_META = {
  text: { label: '文字', icon: 'i-file' },
  list: { label: '列表', icon: 'i-file' },
  facts: { label: '指标', icon: 'i-map' },
  table: { label: '表格', icon: 'i-map' },
  image: { label: '图片', icon: 'i-image' }
};

function renderTextItem(item) {
  return createElement('p', { className: 'item-text-block', text: toText(item.text, '暂无文字内容') });
}

function renderListItem(item) {
  const entries = Array.isArray(item.entries) ? item.entries : [];
  const list = createElement('ul', { className: 'item-list-block' });
  replaceChildren(list, entries.map(entry => createElement('li', { text: displayValue(entry) })));
  return list;
}

function renderFactsItem(item) {
  const facts = Array.isArray(item.facts) ? item.facts : [];
  const list = createElement('dl', { className: 'item-facts-grid' });
  for (const fact of facts) {
    list.append(
      createElement('div', { className: 'item-fact' }, [
        createElement('dt', { text: toText(fact?.label, '未命名指标') }),
        createElement('dd', { text: displayValue(fact?.value) || '未提供' })
      ])
    );
  }
  return list;
}

function renderTableItem(item) {
  const columns = Array.isArray(item.columns) ? item.columns : [];
  const rows = Array.isArray(item.rows) ? item.rows : [];
  const table = createElement('table', { className: 'item-data-table' });
  const headerRow = createElement('tr');
  for (const column of columns) {
    headerRow.append(createElement('th', {
      text: toText(column?.label, column?.key || '字段'),
      attrs: { scope: 'col' }
    }));
  }
  const head = createElement('thead', {}, [headerRow]);
  const body = createElement('tbody');
  for (const row of rows) {
    const tableRow = createElement('tr');
    for (const column of columns) {
      tableRow.append(createElement('td', { text: displayValue(row?.[column?.key]) || '未提供' }));
    }
    body.append(tableRow);
  }
  table.append(head, body);
  return createElement('div', { className: 'item-table-wrap' }, [table]);
}

function itemContent(item) {
  if (item.type === 'text') return renderTextItem(item);
  if (item.type === 'list') return renderListItem(item);
  if (item.type === 'facts') return renderFactsItem(item);
  if (item.type === 'table') return renderTableItem(item);
  return null;
}

function typeMarker(item) {
  const meta = TYPE_META[item.type] || TYPE_META.text;
  return createElement('span', { className: 'item-type-marker', attrs: { 'aria-hidden': 'true' } }, [
    svgIcon(meta.icon),
    createElement('span', { text: meta.label })
  ]);
}

function imageThumbnail(item, onPreview) {
  const source = safeImageUrl(item.fileUrl);
  const button = createElement('button', {
    className: 'item-thumbnail image-preview-button',
    attrs: { type: 'button', 'aria-label': `预览图片：${toText(item.title, '项目成果图')}` }
  });
  if (source) {
    const image = createElement('img', {
      attrs: { alt: toText(item.title, '项目成果图'), loading: 'lazy', decoding: 'async' }
    });
    image.src = source;
    image.addEventListener('error', () => {
      replaceChildren(button, createElement('span', { className: 'image-load-error', text: '图片暂不可用' }));
    }, { once: true });
    button.append(image);
  } else {
    button.append(createElement('span', { className: 'image-load-error', text: '打开图片预览' }));
  }
  button.addEventListener('click', () => onPreview?.(item, button));
  return button;
}

function itemEditor(item, onUpdate) {
  const fields = [
    labeledField({ label: '成果标题', value: item.title, onChange: title => onUpdate?.({ title }) })
  ];
  if (item.type === 'image') {
    fields.push(labeledField({
      label: '图片说明',
      value: item.caption,
      multiline: true,
      onChange: caption => onUpdate?.({ caption })
    }));
  }
  fields.push(labeledField({
    label: '补充说明',
    value: item.note,
    multiline: true,
    onChange: note => onUpdate?.({ note })
  }));
  return createElement('details', { className: 'item-editor' }, [
    createElement('summary', { text: '编辑标题与说明' }),
    createElement('div', { className: 'item-editor-grid' }, fields)
  ]);
}

function moveControls(item, index, total, onMove) {
  return createElement('div', { className: 'deliverable-item-actions' }, [
    actionButton({
      label: `上移：${toText(item.title, '当前成果')}`,
      icon: 'i-arrow',
      className: 'icon-btn move-button move-up',
      disabled: index === 0
    }),
    actionButton({
      label: `下移：${toText(item.title, '当前成果')}`,
      icon: 'i-arrow',
      className: 'icon-btn move-button move-down',
      disabled: index === total - 1
    })
  ]);
}

export function renderItem(item, { chapterId, index, total, onSelect, onMove, onUpdate, onPreview }) {
  const itemType = TYPE_META[item.type] ? item.type : 'text';
  const article = createElement('article', {
    className: `deliverable-item item-type-${itemType}`,
    attrs: { 'aria-selected': String(Boolean(item.selected)) },
    dataset: { itemId: item.id }
  });
  const checkbox = createElement('input', {
    className: 'selection-check',
    attrs: { type: 'checkbox', 'aria-label': `选择成果：${toText(item.title, '未命名成果')}` }
  });
  checkbox.checked = Boolean(item.selected);
  checkbox.addEventListener('change', () => onSelect?.(chapterId, item.id, checkbox.checked));

  const visual = item.type === 'image' ? imageThumbnail(item, onPreview) : typeMarker(item);
  const meta = TYPE_META[item.type] || TYPE_META.text;
  const summary = createElement('div', { className: 'deliverable-item-copy' }, [
    createElement('strong', { text: toText(item.title, '未命名成果') }),
    createElement('span', { text: toText(item.caption || item.note, `${meta.label}成果`) }),
    createElement('span', { className: 'file-tag', text: item.selected ? '已纳入成果' : '未纳入成果' })
  ]);
  const controls = moveControls(item, index, total, onMove);
  controls.children[0].addEventListener('click', () => onMove?.(chapterId, item.id, -1));
  controls.children[1].addEventListener('click', () => onMove?.(chapterId, item.id, 1));
  article.append(checkbox, visual, summary, controls);

  const content = itemContent(item);
  if (content) article.append(createElement('div', { className: 'item-content' }, [content]));
  article.append(itemEditor(item, patch => onUpdate?.(chapterId, item.id, patch)));
  return article;
}
